import {useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'
import type {FunctionComponent, HTMLAttributes, SyntheticEvent} from 'react'
import React from 'react'
import type {AffiliateLink, RootLoaderResponse, SelectedProduct} from '~/types'
import {getTranslation} from '~/localization/translation'
import {getDomainConfig} from '~/domains'
import {useReportWidgetSeen} from '~/hooks/analytic-hooks'

import {CLICK_ID_PLACEHOLDER} from '~/utils/affiliation'

interface KeyFeaturesProps extends HTMLAttributes<HTMLDivElement> {
  keyFeatures?: SelectedProduct['keyFeatures']
  asin: string
  slug: string
  productTitleShort: string
  handleCardClick: (
    _e: SyntheticEvent,
    index: number,
    affiliateLink: AffiliateLink,
    affiliateLinkWithClickId: string,
    itemName: string,
    redirectUrl: string,
    elementName: string,
    elementPlace: string,
  ) => void
  shouldForceTrackAnalytics: boolean
  trackSearchParams: string
  clickId: string
  affiliateLink: AffiliateLink
}

const KeyFeatures: FunctionComponent<KeyFeaturesProps> = ({
  keyFeatures,
  affiliateLink,
  asin,
  slug,
  productTitleShort,
  handleCardClick,
  shouldForceTrackAnalytics,
  trackSearchParams,
  clickId,
  ...attributes
}) => {
  const {locale, language, domain} = useRouteLoaderData('root') as RootLoaderResponse
  const {styles} = getDomainConfig(domain, locale)

  const translations = getTranslation(language)

  const [widgetRef] = useReportWidgetSeen({
    widget_name: 'css-key-features',
    widget_place: 'middle',
  })

  const affiliateLinkWithClickId = affiliateLink?.url?.replaceAll(CLICK_ID_PLACEHOLDER, clickId)

  const redirectUrl = shouldForceTrackAnalytics
    ? `/redirect-analytics?affiliateLink=${encodeURIComponent(affiliateLinkWithClickId)}&${trackSearchParams}`
    : affiliateLinkWithClickId

  return keyFeatures && keyFeatures?.length ? (
    <div {...attributes}>
      <div ref={widgetRef} className="content-container mx-auto text-[#030303] tablet:my-4">
        <h2 className="text-2xl font-semibold">{translations.ProductPage.aboutThisProduct}</h2>
        <p className="text-sm text-gray-600">{translations.ProductPage.quickOverview}</p>
        <ul className="mt-2">
          {keyFeatures?.map(({description}, index: number) => {
            const isLastFeature = index === keyFeatures.length - 1

            return (
              <li key={`${description}-${index}`} className="flex items-center gap-2">
                <span className="text-xl tablet:mb-auto">•</span>
                <span className={clsx('text-base text-gray-700', isLastFeature && 'max-w-[300px] truncate')}>
                  {description}
                </span>
                {isLastFeature && (
                  <a
                    href={redirectUrl}
                    onClick={(e) =>
                      handleCardClick(
                        e,
                        index,
                        affiliateLink,
                        affiliateLinkWithClickId,
                        productTitleShort,
                        redirectUrl,
                        'see-more-on-amazon',
                        'middle',
                      )
                    }
                    aria-label={`view ${productTitleShort}`}
                    target="_blank"
                    rel="sponsored noreferrer"
                    className={clsx(
                      'font-medium underline underline-offset-2 mobile:shrink-0',
                      styles.LISTPAGE_ANCHOR_LINK,
                      'listpage_anchor_link',
                    )}
                  >
                    {translations.ProductPage.seeMoreOnAmazon}
                  </a>
                )}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  ) : null
}

export default KeyFeatures
